import type {
  CheckoutSimulationItemTaxDetail,
  CheckoutSimulationResult,
} from './api'
import {
  resolveCheckoutDisplayQuantity,
  resolveCheckoutDisplayUnitPrice,
  resolveCheckoutPackagingInfo,
  type CheckoutPackagingInfo,
} from './checkout-packaging'
import type { CartItem } from './types'

export type QuoteCheckoutEditMode = 'unset' | 'persist_quote' | 'session_only'
export type QuoteCheckoutPackagingInfo = CheckoutPackagingInfo

export type QuoteCheckoutItemOverride = {
  itemId: number
  quantity: number
}

const QUOTE_CHECKOUT_BOX_VARIANT_ID_SEPARATOR = '__cx__'

const roundQuoteCheckoutNumber = (value: number, decimals = 2) => {
  if (!Number.isFinite(value)) return 0
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

export const calculatePartnerCommissionValue = (
  baseUnitPrice: number,
  quantity: number,
  commissionPercent: number
) => roundQuoteCheckoutNumber(
  Math.max(0, Number(baseUnitPrice) || 0)
  * Math.max(0, Number(quantity) || 0)
  * Math.max(0, Number(commissionPercent) || 0)
  / 100,
  2
)

const parsePositiveIntegerLike = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value)
  }

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const parsed = Number.parseInt(value.trim(), 10)
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null
  }

  return null
}

const resolveQuoteCheckoutText = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key]
    if (typeof value !== 'string') continue
    const normalized = value.trim()
    if (normalized) return normalized
  }

  return ''
}

const resolveQuoteCheckoutBoolean = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'boolean') return value
    if (typeof value === 'number' && Number.isFinite(value)) return value === 1
    if (typeof value === 'string') {
      const normalized = value.trim().toLocaleLowerCase('pt-BR')
      if (!normalized) continue
      if (['1', 'true', 'sim', 'yes', 'on'].includes(normalized)) return true
      if (['0', 'false', 'nao', 'não', 'no', 'off'].includes(normalized)) return false
    }
  }

  return false
}

const resolveQuoteCheckoutNumber = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string') {
      const normalized = value.trim().replace(/\./g, '').replace(',', '.')
      if (!normalized) continue
      const parsed = Number(normalized)
      if (Number.isFinite(parsed)) return parsed
    }
  }

  return 0
}

export const resolveQuoteCheckoutItemPackagingInfo = (item: Record<string, unknown>) =>
  resolveCheckoutPackagingInfo(item)

export const resolveQuoteCheckoutDisplayQuantity = (
  quantity: number,
  packaging: Pick<QuoteCheckoutPackagingInfo, 'isBox' | 'packagingQuantity'>
) => resolveCheckoutDisplayQuantity(quantity, packaging)

export const resolveQuoteCheckoutDisplayUnitPrice = (
  unitPrice: number,
  packaging: Pick<QuoteCheckoutPackagingInfo, 'isBox' | 'packagingQuantity'>
) => resolveCheckoutDisplayUnitPrice(unitPrice, packaging)

export const getQuoteCheckoutItemId = (item: Pick<CartItem, 'sourceQuoteItemId'>) => {
  const itemId = Number(item.sourceQuoteItemId ?? 0)
  return Number.isFinite(itemId) && itemId > 0 ? Math.trunc(itemId) : null
}

export const normalizeQuoteCheckoutItemOverrides = (
  overrides: QuoteCheckoutItemOverride[]
): QuoteCheckoutItemOverride[] => {
  const overridesByItemId = new Map<number, QuoteCheckoutItemOverride>()

  for (const override of Array.isArray(overrides) ? overrides : []) {
    const itemId = Number(override?.itemId ?? 0)
    if (!Number.isFinite(itemId) || itemId <= 0) continue
    const quantity = roundQuoteCheckoutNumber(Math.max(0, Number(override?.quantity ?? 0)), 4)
    overridesByItemId.set(Math.trunc(itemId), {
      itemId: Math.trunc(itemId),
      quantity,
    })
  }

  return Array.from(overridesByItemId.values()).sort((left, right) => left.itemId - right.itemId)
}

export const mapQuoteDetailToCheckoutItems = (quote: unknown): CartItem[] => {
  if (!quote || typeof quote !== 'object') return []

  const record = quote as Record<string, unknown>
  const quoteId = parsePositiveIntegerLike(record.id ?? record.codigo) ?? 0
  const quoteLocks = (
    record.bloqueios_edicao
    || record.bloqueiosEdicao
    || record.editLocks
    || null
  ) as Record<string, unknown> | null
  const quoteRemovalLocked = quoteLocks && typeof quoteLocks === 'object'
    ? resolveQuoteCheckoutBoolean(quoteLocks, ['bloqueia_remocao_itens', 'bloqueiaRemocaoItens', 'removeLocked'])
    : false
  const rawItems = [record.itens, record.items, record.produtos].find(Array.isArray)
  if (!Array.isArray(rawItems)) return []

  return rawItems.flatMap((rawItem) => {
    if (!rawItem || typeof rawItem !== 'object') return []

    const item = rawItem as Record<string, unknown>
    const productId = parsePositiveIntegerLike(item.produto_id ?? item.produtoId)
    const productGradeId = parsePositiveIntegerLike(item.produto_grade_id ?? item.produtoGradeId)
    const quoteItemId = parsePositiveIntegerLike(item.id ?? item.item_id ?? item.itemId)
    const quantity = Math.max(0, resolveQuoteCheckoutNumber(item, ['quantidade', 'qtde', 'quantity']))
    const unitPrice = Math.max(0, resolveQuoteCheckoutNumber(item, ['preco', 'valor', 'valor_unitario', 'valorUnitario']))
    const packaging = resolveQuoteCheckoutItemPackagingInfo(item)
    const subtotal = Math.max(0, resolveQuoteCheckoutNumber(item, ['subtotal', 'valor_subtotal', 'valorSubtotal', 'total_base']))
    const discount = Math.max(0, resolveQuoteCheckoutNumber(item, ['valor_desconto', 'valorDesconto', 'desconto']))
    const ipi = Math.max(0, resolveQuoteCheckoutNumber(item, ['valor_ipi', 'valorIpi', 'ipi']))
    const difal = Math.max(0, resolveQuoteCheckoutNumber(item, ['valor_st', 'valorSt', 'valor_difal', 'valorDifal', 'st']))
    const totalBase = Math.max(0, resolveQuoteCheckoutNumber(item, ['valor_total', 'valorTotal', 'total']))
    const resolvedSubtotal = subtotal > 0
      ? subtotal
      : totalBase > 0
        ? totalBase
        : Math.max(0, (unitPrice * quantity) - discount)
    const computedTotal = Math.max(0, resolvedSubtotal + ipi + difal)
    const total = Math.max(
      0,
      resolveQuoteCheckoutNumber(item, ['valor_total_liquido', 'valorTotalLiquido', 'total_liquido', 'totalLiquido'])
        || computedTotal
    )
    const partnerCommissionPercent = Math.max(0, resolveQuoteCheckoutNumber(item, [
      'comissao_percentual',
      'comissaoPercentual',
      'lucro_cliente_percentual',
      'lucroClientePercentual',
    ]))
    const persistedPartnerCommissionValue = Math.max(0, resolveQuoteCheckoutNumber(item, [
      'comissao_valor',
      'comissaoValor',
      'lucro_cliente_valor',
      'lucroClienteValor',
    ]))
    const partnerValueUnitPrice = Math.max(0, resolveQuoteCheckoutNumber(item, [
      'preco_margem',
      'valor_unitario_margem',
      'valorUnitarioMargem',
      'unit_price_margin',
    ])) || unitPrice
    const explicitPartnerBaseUnitPrice = Math.max(0, resolveQuoteCheckoutNumber(item, [
      'catalog_preco_distribuicao',
      'catalogPrecoDistribuicao',
      'preco_distribuicao_catalogo',
      'precoDistribuicaoCatalogo',
      'preco_unitario_base',
      'precoUnitarioBase',
      'valor_unitario_base',
      'valorUnitarioBase',
      'unit_price_base',
      'unitPriceBase',
      'preco_catalogo',
      'precoCatalogo',
    ]))
    const quoteCarriesSeparateBaseUnitPrice = unitPrice > 0
      && Math.abs(unitPrice - partnerValueUnitPrice) > 0.005
    const derivedPartnerBaseUnitPrice = quantity > 0 && persistedPartnerCommissionValue > 0
      ? Math.max(0, partnerValueUnitPrice - (persistedPartnerCommissionValue / quantity))
      : 0
    const persistedPartnerBaseUnitPrice = Math.max(0, resolveQuoteCheckoutNumber(item, [
      'preco_real',
      'valor_unitario_real',
      'unit_price_real',
    ]))
    const partnerUnitPrice = explicitPartnerBaseUnitPrice
      || (quoteCarriesSeparateBaseUnitPrice ? unitPrice : 0)
      || derivedPartnerBaseUnitPrice
      || persistedPartnerBaseUnitPrice
      || unitPrice
    const partnerCommissionValue = calculatePartnerCommissionValue(
      partnerUnitPrice,
      quantity,
      partnerCommissionPercent
    )
    const code = resolveQuoteCheckoutText(item, ['referencia', 'codigo', 'code']) || String(productGradeId || productId || quoteItemId || quoteId)
    const rawName = resolveQuoteCheckoutText(item, ['nome', 'descricao', 'description']) || 'Produto'
    const image = resolveQuoteCheckoutText(item, ['imagem', 'imagem_url', 'imagemUrl', 'produto_imagem', 'image'])
    const normalizedProductId = productId ?? productGradeId ?? quoteItemId
    const priceReduced = resolveQuoteCheckoutBoolean(item, ['valor_reduzido', 'valorReduzido', 'priceReduced'])
    const editLocked =
      priceReduced
      || resolveQuoteCheckoutBoolean(item, ['edicao_bloqueada', 'edicaoBloqueada', 'editLocked'])
    const removeLocked =
      editLocked
      || quoteRemovalLocked
      || resolveQuoteCheckoutBoolean(item, ['remocao_bloqueada', 'remocaoBloqueada', 'removeLocked'])
    const lockReason = (
      resolveQuoteCheckoutText(item, ['motivo_bloqueio_edicao', 'motivoBloqueioEdicao', 'lockReason'])
      || (editLocked ? 'valor_reduzido' : '')
      || undefined
    )
    const removeLockReason = (
      resolveQuoteCheckoutText(item, ['motivo_bloqueio_remocao', 'motivoBloqueioRemocao', 'removeLockReason'])
      || (removeLocked ? (editLocked ? 'valor_reduzido' : 'venda_negociada') : '')
      || undefined
    )

    if (!normalizedProductId || quantity <= 0) return []

    const syntheticProductId = packaging.isBox
      ? `${normalizedProductId}${QUOTE_CHECKOUT_BOX_VARIANT_ID_SEPARATOR}${packaging.packagingQuantity}`
      : String(normalizedProductId)
    const name = (
      packaging.isBox
      && packaging.packagingLabel
      && !rawName.toLocaleLowerCase('pt-BR').includes(packaging.packagingLabel.toLocaleLowerCase('pt-BR'))
    )
      ? `${rawName} - ${packaging.packagingLabel}`
      : rawName
    const displayUnitPrice = resolveQuoteCheckoutDisplayUnitPrice(unitPrice, packaging)
    const salesCampaign = resolveQuoteCheckoutBoolean(item, [
      'campanha_sobre_estoque',
      'campanhaSobreEstoque',
      'salesCampaign',
    ])
    const salesCampaignOriginalPrice = Math.max(0, resolveQuoteCheckoutNumber(item, [
      'campanha_preco_original',
      'campanhaPrecoOriginal',
      'salesCampaignOriginalPrice',
    ]))
    const salesCampaignMinQuantity = Math.max(0, resolveQuoteCheckoutNumber(item, [
      'campanha_quantidade_minima',
      'campanhaQuantidadeMinima',
      'salesCampaignMinQuantity',
    ]))

    return [{
      product: {
        id: syntheticProductId,
        code,
        manufacturerCode: code,
        name,
        brand: '',
        category: '',
        subcategory: '',
        image,
        specs: {},
        inStock: true,
        priceVisible: true,
        needsQuote: false,
        cartBaseProductId: String(normalizedProductId),
        packagingMode: packaging.packagingMode,
        packagingQuantity: packaging.packagingQuantity,
        packagingLabel: packaging.packagingLabel,
        unitPrice: displayUnitPrice,
        price: unitPrice,
        distributionPrice: unitPrice,
        cnpjIePrice: unitPrice,
        salesCampaign,
        salesCampaignCategory: resolveQuoteCheckoutText(item, [
          'campanha_categoria',
          'campanhaCategoria',
          'salesCampaignCategory',
        ]) || undefined,
        salesCampaignSellerType: resolveQuoteCheckoutText(item, [
          'campanha_tipo_vendedor',
          'campanhaTipoVendedor',
          'salesCampaignSellerType',
        ]) || undefined,
        salesCampaignMinQuantity: salesCampaignMinQuantity > 0
          ? salesCampaignMinQuantity
          : undefined,
        salesCampaignOriginalPrice: salesCampaignOriginalPrice > 0
          ? salesCampaignOriginalPrice
          : undefined,
        salesCampaignPrice: salesCampaign ? unitPrice : undefined,
      },
      quantity,
      checkoutSource: 'quote',
      sourceQuoteId: quoteId || undefined,
      sourceQuoteItemId: quoteItemId || undefined,
      sourceProductGradeId: productGradeId || undefined,
      lockedUnitPrice: unitPrice,
      lockedSubtotal: resolvedSubtotal,
      lockedDiscount: discount,
      lockedIpi: ipi,
      lockedDifal: difal,
      lockedTaxes: ipi + difal,
      lockedTotal: total,
      partnerUnitPrice,
      partnerCommissionPercent,
      partnerCommissionValue,
      partnerValueUnitPrice,
      priceReduced,
      editLocked,
      removeLocked,
      lockReason,
      removeLockReason,
      readOnly: editLocked,
    }]
  })
}

export const sanitizeQuoteCheckoutItemOverridesForLocks = (
  items: CartItem[],
  overrides: QuoteCheckoutItemOverride[]
): QuoteCheckoutItemOverride[] => {
  const itemsByQuoteItemId = new Map<number, CartItem>()
  for (const item of Array.isArray(items) ? items : []) {
    const itemId = getQuoteCheckoutItemId(item)
    if (itemId) itemsByQuoteItemId.set(itemId, item)
  }

  return normalizeQuoteCheckoutItemOverrides(overrides).filter((override) => {
    const item = itemsByQuoteItemId.get(override.itemId)
    if (!item) return false
    if (item.editLocked || item.readOnly) return false
    if (!(override.quantity > 0) && item.removeLocked) return false
    return true
  })
}

export const applyQuoteCheckoutItemOverrides = (
  items: CartItem[],
  overrides: QuoteCheckoutItemOverride[]
): CartItem[] => {
  const overridesByItemId = new Map(
    normalizeQuoteCheckoutItemOverrides(overrides).map((override) => [override.itemId, override] as const)
  )

  return items.flatMap((item) => {
    const quoteItemId = getQuoteCheckoutItemId(item)
    const override = quoteItemId ? overridesByItemId.get(quoteItemId) : null
    if (!override) {
      return [{ ...item, readOnly: Boolean(item.readOnly || item.editLocked) }]
    }

    if (!(override.quantity > 0)) return []

    const currentQuantity = item.quantity > 0 ? item.quantity : 1
    const unitPrice = roundQuoteCheckoutNumber(
      item.lockedUnitPrice
      ?? ((item.lockedSubtotal ?? 0) / currentQuantity),
      9
    )
    const unitDiscount = roundQuoteCheckoutNumber((item.lockedDiscount ?? 0) / currentQuantity, 9)
    const unitIpi = roundQuoteCheckoutNumber((item.lockedIpi ?? 0) / currentQuantity, 9)
    const unitDifal = roundQuoteCheckoutNumber((item.lockedDifal ?? 0) / currentQuantity, 9)
    const quantity = roundQuoteCheckoutNumber(override.quantity, 4)
    const lockedSubtotal = roundQuoteCheckoutNumber(unitPrice * quantity, 2)
    const lockedDiscount = roundQuoteCheckoutNumber(unitDiscount * quantity, 2)
    const lockedIpi = roundQuoteCheckoutNumber(unitIpi * quantity, 2)
    const lockedDifal = roundQuoteCheckoutNumber(unitDifal * quantity, 2)
    const lockedTaxes = roundQuoteCheckoutNumber(lockedIpi + lockedDifal, 2)

    return [{
      ...item,
      quantity,
      lockedUnitPrice: unitPrice,
      lockedSubtotal,
      lockedDiscount,
      lockedIpi,
      lockedDifal,
      lockedTaxes,
      lockedTotal: roundQuoteCheckoutNumber(lockedSubtotal + lockedTaxes, 2),
      readOnly: Boolean(item.readOnly || item.editLocked),
    }]
  })
}

export const mergeQuoteCheckoutSimulationItems = (
  items: CartItem[],
  simulationItems: CheckoutSimulationItemTaxDetail[]
): CartItem[] => {
  const matchedSimulationItems = matchQuoteCheckoutSimulationItems(items, simulationItems)

  return items.map((item, index) => {
    const simulationItem = matchedSimulationItems[index]
    if (!simulationItem) {
      return { ...item, readOnly: false }
    }

    const quantity = roundQuoteCheckoutNumber(
      simulationItem.quantity > 0 ? simulationItem.quantity : item.quantity,
      4
    )
    const subtotal = roundQuoteCheckoutNumber(Math.max(0, simulationItem.subtotal), 2)
    const discount = roundQuoteCheckoutNumber(Math.max(0, simulationItem.discount), 2)
    const ipi = roundQuoteCheckoutNumber(Math.max(0, simulationItem.ipi), 2)
    const difal = roundQuoteCheckoutNumber(Math.max(0, simulationItem.difal), 2)
    const impostos = roundQuoteCheckoutNumber(
      Math.max(0, simulationItem.impostos || (ipi + difal)),
      2
    )

    return {
      ...item,
      quantity,
      lockedUnitPrice: quantity > 0 ? roundQuoteCheckoutNumber(subtotal / quantity, 9) : item.lockedUnitPrice,
      lockedSubtotal: subtotal,
      lockedDiscount: discount,
      lockedIpi: ipi,
      lockedDifal: difal,
      lockedTaxes: impostos,
      lockedTotal: roundQuoteCheckoutNumber(Math.max(0, subtotal - discount + impostos), 2),
      readOnly: false,
    }
  })
}

export const matchQuoteCheckoutSimulationItems = (
  items: CartItem[],
  simulationItems: CheckoutSimulationItemTaxDetail[]
): Array<CheckoutSimulationItemTaxDetail | null> => {
  const normalizedSimulationItems = Array.isArray(simulationItems) ? simulationItems : []
  const indexesByKey = new Map<string, number[]>()
  const consumedIndexes = new Set<number>()

  const addIndex = (key: string, index: number) => {
    if (!key) return
    const indexes = indexesByKey.get(key) ?? []
    indexes.push(index)
    indexesByKey.set(key, indexes)
  }

  normalizedSimulationItems.forEach((item, index) => {
    const quoteItemId = Math.max(0, Math.trunc(Number(item.quoteItemId ?? 0)))
    const productGradeId = Math.max(0, Math.trunc(Number(item.productGradeId ?? 0)))
    const productId = Math.max(0, Math.trunc(Number(item.productId ?? 0)))

    if (quoteItemId > 0) addIndex(`quote:${quoteItemId}`, index)
    if (productGradeId > 0) addIndex(`grade:${productGradeId}`, index)
    if (productId > 0) addIndex(`product:${productId}`, index)
  })

  const takeByKey = (key: string) => {
    const indexes = indexesByKey.get(key)
    if (!indexes) return -1

    while (indexes.length > 0) {
      const index = indexes.shift() ?? -1
      if (index >= 0 && !consumedIndexes.has(index)) return index
    }

    return -1
  }

  return items.map((item, itemIndex) => {
    const quoteItemId = getQuoteCheckoutItemId(item)
    const productGradeId = Math.max(0, Math.trunc(Number(item.sourceProductGradeId ?? 0)))
    const productId = Math.max(0, Math.trunc(Number(
      item.product.cartBaseProductId
      ?? (/^\d+$/.test(String(item.product.id)) ? item.product.id : 0)
      ?? 0
    )))
    const candidateKeys = [
      quoteItemId ? `quote:${quoteItemId}` : '',
      productGradeId > 0 ? `grade:${productGradeId}` : '',
      productId > 0 ? `product:${productId}` : '',
    ]

    let simulationIndex = -1
    for (const key of candidateKeys) {
      if (!key) continue
      simulationIndex = takeByKey(key)
      if (simulationIndex >= 0) break
    }

    if (
      simulationIndex < 0
      && normalizedSimulationItems.length === items.length
      && !consumedIndexes.has(itemIndex)
    ) {
      simulationIndex = itemIndex
    }

    if (simulationIndex < 0) {
      simulationIndex = normalizedSimulationItems.findIndex((_, index) => !consumedIndexes.has(index))
    }

    if (simulationIndex < 0) return null
    consumedIndexes.add(simulationIndex)
    return normalizedSimulationItems[simulationIndex] ?? null
  })
}

export const doesQuoteCheckoutSimulationMatchItems = (
  items: CartItem[],
  simulationItems: CheckoutSimulationItemTaxDetail[]
) => {
  if (
    !Array.isArray(items)
    || items.length === 0
    || !Array.isArray(simulationItems)
    || simulationItems.length !== items.length
  ) return false

  const matchedSimulationItems = matchQuoteCheckoutSimulationItems(items, simulationItems)

  return matchedSimulationItems.every((simulationItem, index) => {
    if (!simulationItem) return false

    const item = items[index]
    const itemIdentityKeys = [
      getQuoteCheckoutItemId(item) ? `quote:${getQuoteCheckoutItemId(item)}` : '',
      Number(item.sourceProductGradeId) > 0 ? `grade:${Math.trunc(Number(item.sourceProductGradeId))}` : '',
      Number(item.product.cartBaseProductId) > 0
        ? `product:${Math.trunc(Number(item.product.cartBaseProductId))}`
        : /^\d+$/.test(String(item.product.id))
          ? `product:${Math.trunc(Number(item.product.id))}`
          : '',
    ].filter(Boolean)
    const simulationIdentityKeys = [
      Number(simulationItem.quoteItemId) > 0
        ? `quote:${Math.trunc(Number(simulationItem.quoteItemId))}`
        : '',
      Number(simulationItem.productGradeId) > 0
        ? `grade:${Math.trunc(Number(simulationItem.productGradeId))}`
        : '',
      Number(simulationItem.productId) > 0
        ? `product:${Math.trunc(Number(simulationItem.productId))}`
        : '',
    ].filter(Boolean)
    const identityMatches = itemIdentityKeys.length === 0
      || simulationIdentityKeys.length === 0
      || itemIdentityKeys.some((key) => simulationIdentityKeys.includes(key))
    const quantity = roundQuoteCheckoutNumber(Math.max(0, Number(item.quantity) || 0), 4)
    const lockedUnitPrice = Math.max(0, Number(item.lockedUnitPrice) || 0)
    const expectedSubtotal = roundQuoteCheckoutNumber(
      Math.max(0, Number(item.lockedSubtotal) || (lockedUnitPrice * quantity)),
      2
    )
    const simulatedQuantity = roundQuoteCheckoutNumber(
      Math.max(0, Number(simulationItem.quantity) || 0),
      4
    )
    const simulatedSubtotal = roundQuoteCheckoutNumber(
      Math.max(0, Number(simulationItem.subtotal) || 0),
      2
    )

    return identityMatches
      && Math.abs(simulatedQuantity - quantity) <= 0.0001
      && Math.abs(simulatedSubtotal - expectedSubtotal) <= 0.02
  })
}

export const doesQuoteCheckoutFinancialSummaryMatchItems = (
  items: CartItem[],
  financialSummary: CheckoutSimulationResult
) => {
  if (!doesQuoteCheckoutSimulationMatchItems(items, financialSummary.items)) return false

  const matchedSimulationItems = matchQuoteCheckoutSimulationItems(items, financialSummary.items)
  const itemsSubtotal = roundQuoteCheckoutNumber(
    matchedSimulationItems.reduce(
      (total, simulationItem) => total + Math.max(0, Number(simulationItem?.subtotal) || 0),
      0
    ),
    2
  )
  const summarySubtotal = roundQuoteCheckoutNumber(
    Math.max(0, Number(financialSummary.subtotal) || 0),
    2
  )

  return Math.abs(summarySubtotal - itemsSubtotal) <= 0.02
}
